import React from "react";

import * as auth from "@client/features/auth";

import * as constants from "@client/constants";
import * as analytics from "@client/shared/analytics";
import * as typography from "@client/shared/typography";

import {Button} from "@client/components/Button";
import {Icon} from "@client/components/Icon";
import {Image} from "@client/components/Image";
import {Layout} from "@client/site/Layout";
import {Highlight, Register} from "@client/site/components";
import {useSiteContext} from "@client/site/context";
import * as forms from "@client/site/forms";
import {classNames, reverse, server} from "@reactivated";

const styles = {
    hero: classNames(
        "home-hero",
        "text-sm leading-[1.2]",
        "md:min-h-[530px] md:grid md:grid-cols-2",
    ),
    illustrationAndFeatures: classNames(
        "overflow-hidden -ml-[10px] -mr-[10px] flex justify-center w-screen",
        "md:overflow-visible md:ml-0 md:mr-0 md:block md:justify-start md:w-auto",
    ),
    illustration: classNames(
        "home-illustration",
        "space-x-[15px] flex flex-1 items-start justify-center ml-[10px] mr-[10px] pb-[10px]",
        "md:space-x-0 md:block md:flex-none md:items-stretch md:justify-start md:ml-0 md:mr-0 md:pb-0",
    ),
    feature: classNames(
        "p-[10px] bg-white rounded-[6px] inline-block whitespace-nowrap text-[calc(0.3vw+8px)]",
        "flex-1 shadow-[0_1px_5px_0_rgba(205,213,221,0.45)]",
        "md:flex-none md:shadow-[0_10px_40px_0_rgba(205,213,221,0.45)]",
        "xl:text-xs",
    ),
    pitch: classNames(
        "flex flex-col justify-center",
        "space-y-[14px]",
        "md:space-y-[20px]",
    ),
    appStoreButtons: classNames(
        "home-app-store-buttons",
        "flex flex-wrap -m-[10px]",
        "justify-between",
        "md:justify-start",
    ),
    highlights: classNames(
        "home-highlights",
        "text-center grid gap-[50px] grid-cols-4",
    ),
};

const getAppStoreURL = (context: ReturnType<typeof useSiteContext>) => {
    const url = new URL(context.request.url);
    const utmContent = url.searchParams.get("utm_source");
    const appStoreUrl = new URL(constants.APP_STORE_URL);

    if (utmContent != null) {
        appStoreUrl.searchParams.append("ct", utmContent);
    }

    return appStoreUrl.toString();
};

export const Template = () => {
    const context = useSiteContext();

    const [isSubmitting, setIsSubmitting] = React.useState(false);
    const form = auth.useRegistrationForm();

    const handleOnSubmit = async (event: React.SyntheticEvent) => {
        event.preventDefault();
        setIsSubmitting(true);
        const result = await server.core.rpc.register({
            ...form.values,
            invite_code: form.values.invite_code,
            with_notification: true,
            recipe_uuid: null,
            source: "home_page",
            platform: "web",
        });

        if (result.type == "invalid") {
            form.processErrors(result.errors);
            setIsSubmitting(false);
        } else if (result.type == "success") {
            const profile = result.data;
            await analytics.registered(profile.user.tracking_key, "home_page", "trial");
            window.location.href = reverse("journal");
        }
    };

    return (
        <Layout
            center
            title={{
                page: "Joy Health Tracker - Track what you eat. Quickly.",
                meta: "Joy Health Tracker",
            }}
        >
            <div className={styles.hero}>
                <div className={styles.illustrationAndFeatures}>
                    <div className={styles.illustration}>
                        <Image image="phone" title="Phone" />
                        <div className={styles.feature}>
                            <Image image="calories-left" title="Calories left" />
                            <Icon large="relative" icon="0821-check">
                                <typography.P muted small bold>
                                    Check your totals
                                </typography.P>
                            </Icon>
                        </div>
                        <div className={styles.feature}>
                            <Image image="graph" title="Graph of progress" />
                            <Icon large="relative" icon="custom-trophy">
                                <typography.P muted small bold>
                                    Visualize your progress
                                </typography.P>
                            </Icon>
                        </div>
                        <div className={styles.feature}>
                            <Image image="foods" title="Searchable foods" />
                            <Icon large="relative" icon="0803-magnifier">
                                <typography.P muted small bold>
                                    Search professionally curated foods
                                </typography.P>
                            </Icon>
                        </div>
                    </div>
                </div>
                <form noValidate onSubmit={handleOnSubmit} className={styles.pitch}>
                    <typography.Header>
                        Track what you eat.
                        <br />
                        <span style={{color: "#EF8165"}}>Quickly.</span>
                    </typography.Header>
                    <typography.P>
                        Joy is a fast and flexible way to track your food, weight and
                        progress
                    </typography.P>
                    <forms.PField field={form.fields.email} />
                    <forms.PField field={form.fields.invite_code} />
                    <Register isSubmitting={isSubmitting} />
                    <typography.P small muted>
                        Try Joy free for a month. Afterwards, it’s just 5 bucks a month.
                    </typography.P>
                    <typography.P small muted>
                        Can’t afford Joy?{" "}
                        <typography.A href="mailto:support@joyapp.com">
                            Let’s talk
                        </typography.A>
                    </typography.P>
                    <div className={styles.appStoreButtons}>
                        <Button track="app_store" href={getAppStoreURL(context)}>
                            <Icon large="relative" icon="custom-app-store">
                                App Store
                            </Icon>
                        </Button>
                        <Button track="play_store" href={constants.PLAY_STORE_URL}>
                            <Icon large="relative" icon="custom-play-store">
                                Google Play
                            </Icon>
                        </Button>
                    </div>
                </form>
            </div>
            <div className={styles.highlights}>
                <div>
                    <h3>Look no further. You found the perfect diet app.</h3>
                </div>
                <Highlight
                    variant="orange"
                    icon="0849-arrow-wave"
                    title="Flexible"
                    description="Joy supports any diet and all goals."
                    border
                >
                    <li>Fasting? Adjust or skip meals.</li>
                    <li>
                        Counting carbs? Quickly view net carbs and fiber for any entry.
                    </li>
                    <li>Gaining weight? Go ahead, set a surplus caloric target.</li>
                    <li>Cycling macros? Vary your targets per day.</li>
                </Highlight>
                <Highlight
                    variant="blue"
                    icon="0752-timer"
                    title="Efficient"
                    description="You're hungry - spend less time logging."
                    border
                >
                    <li>Creature of habit? Recent entries are always accessible.</li>
                    <li>
                        Add entries in bulk.
                        <br />
                        Not just individually.
                    </li>
                    <li>Copy entries, meals or entire days.</li>
                    <li>Create and tweak recipes on the fly.</li>
                </Highlight>
                <Highlight
                    variant="green"
                    icon="0625-archery"
                    title="Accurate"
                    description="We use a professionally curated food database."
                    border
                >
                    <li>No inaccurate community-entered foods.</li>
                    <li>Thousands of consumer packaged goods.</li>
                    <li>
                        Full catalog of{" "}
                        <button
                            style={{cursor: "default"}}
                            onClick={() => {
                                throw new Error("Site error!");
                            }}
                        >
                            popular
                        </button>{" "}
                        restaurants.
                    </li>
                    <li>USDA generic foods for staples.</li>
                </Highlight>
                <Highlight
                    variant="stone"
                    icon="0105-shield-check"
                    title="Privacy-first"
                    description={
                        <>
                            Joy is the product, not you. <br />
                            We run on subscriptions.
                        </>
                    }
                    border
                >
                    <li>No ads. Your attention and focus are valuable.</li>
                    <li>We never sell your data.</li>
                    <li>
                        No third-party tracking of any kind.{" "}
                        <a style={{color: "#488EA5"}} href={reverse("privacy_policy")}>
                            Learn more.
                        </a>
                    </li>
                    <li>You decide who sees your data.</li>
                </Highlight>
            </div>
        </Layout>
    );
};
